import type { z } from "zod";
import type { AppErrorDto } from "./types";

export const API_BASE = import.meta.env.VITE_API_BASE || "/api";

export class ApiError extends Error {
  data: AppErrorDto;
  /** HTTP status of the failed response; absent when no response failed. */
  status?: number;

  constructor(message: string, data: AppErrorDto, status?: number) {
    super(message);
    this.name = "ApiError";
    this.data = data;
    this.status = status;
  }
}

/**
 * A 4xx: the server understood the request and refused it, so asking again
 * gets the same answer. A query whose failure can be one of these should not
 * spend React Query's default retries (and their backoff, during which the
 * user sees only a spinner) on it.
 */
export const isClientError = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status !== undefined &&
  error.status >= 400 &&
  error.status < 500;

/**
 * Human-readable text for anything thrown by the API layer, for display. The
 * app is used on mobile, where an error that only reaches `console.error` is
 * invisible — so failures belong on screen, and this is what they say.
 */
export const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Validate a parsed JSON payload against its zod schema. A schema mismatch is
 * treated like any other failed request: it throws an {@link ApiError} that
 * surfaces through the same on-screen error path, with a readable summary of
 * what didn't match (so backend/frontend drift is loud, not silently wrong).
 */
export function parseResponse<T>(
  schema: z.ZodType<T>,
  data: unknown,
  endpoint: string,
): T {
  const result = schema.safeParse(data);
  if (result.success) return result.data;

  const summary = result.error.issues
    .slice(0, 4)
    .map((issue) => `${issue.path.join(".") || "response"}: ${issue.message}`)
    .join("; ");
  const message = `Unexpected response from ${endpoint} (${summary})`;
  throw new ApiError(message, { type: "generic", error: message });
}

/**
 * What to say when a failed response carries no `AppErrorDto` — it came from
 * the platform rather than from the app, so there is nothing to unwrap.
 * Cloudflare answers a Worker that ran out of CPU (a big import diff is the
 * way to hit that here) with its own HTML error page, which used to surface as
 * the contentless "Failed to parse error response". Name the status and what it
 * usually means instead.
 */
const platformErrorText = (status: number, statusText: string): string => {
  switch (status) {
    case 502:
    case 503:
    case 504:
      return `The server ran out of time or resources on this request (${status}). A large import is the usual cause — try again, and if it keeps failing the file is probably too big to process in one request.`;
    case 413:
      return "The file is too large for the server to accept (413).";
    case 429:
      return "Too many requests (429) — wait a moment and try again.";
    default:
      return status
        ? `The server returned an error (${status}${statusText ? ` ${statusText}` : ""}) with no details.`
        : "The server returned an error with no details.";
  }
};

export async function handleResponse(response: Response): Promise<void> {
  if (response.ok) return;

  let errorData: AppErrorDto | null = null;

  try {
    const json = await response.json();
    if (json && typeof json === "object" && "type" in json && "error" in json) {
      errorData = json as AppErrorDto;
    }
  } catch {
    // Not JSON at all — the platform, not the app, produced this response.
  }

  if (!errorData) {
    const error = platformErrorText(response.status, response.statusText);
    errorData = { type: "generic", error };
  }

  throw new ApiError(errorData.error, errorData, response.status);
}
